import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { Message, SelectItem, SortEvent } from 'primeng/api';
import { MessageService } from '@shared/services/message-service';
import { Employee } from '@shared/models/employee';
import { Site } from '@shared/models/site';
import { debounceTime , map, take, tap} from 'rxjs/operators';
import { BehaviorSubject , combineLatest, Observable, Subscription, zip} from 'rxjs';
import { PieceRateService } from 'projects/warehousing/src/app/services/piece-rate.service';
import { ActivityBlockingService } from '@shared/services/activity-blocking.service';
import { PieceRateAdjustmentEntryBuilder } from '@shared/models/piece-rate-adjustment';
import { ShiftContextService } from 'projects/warehousing/src/app/services/shift-context.service';
import { SiteConfig } from '@shared/models/site-config';
import { timeFormat } from '@shared/helpers/timeFormat';
import { PieceRateActivity } from '@shared/models/piece-rate-activity';
import { Unit } from '@shared/models/unit';
import { AutoSaveService } from '@shared/services/auto-save.service';
import { range } from '@shared/helpers/range';
import { calcAfterPenalty , calcAllowedBad, calcQuality} from '../piece-rate-formulas';
import { CustomValidators } from '@shared/helpers/custom-validators';
import { PieceRateAttendanceGoalType } from "@shared/models/piece-rate-attendance-goal-type";
class BuilderEntryRow extends UntypedFormGroup {
    private component: PieceRateEntriesComponent;

    quality: Observable<number>;
    allowedBad: Observable<number>;
    afterPenalty: Observable<number>;
    afterAdjustment: Observable<number>;

    id: number;
    pieceRateActivityId: number;

    prevValues: {
        builderId: number;
        tableNumber: number;
        count: number;
        secondaryCount: number;
        auditedCount: number;
        failedCount: number;
        nonProductiveHours: string;
        metAttendanceGoal: boolean;
    };

    currentValues: BehaviorSubject<{
        builderId: number;
        tableNumber: number;
        count: number;
        secondaryCount: number;
        auditedCount: number;
        failedCount: number;
        nonProductiveHours: string;
        metAttendanceGoal: boolean;
    }>;

    private subscriptions: Subscription[];

    constructor(entry: PieceRateAdjustmentEntryBuilder, component: PieceRateEntriesComponent) {
        super({
            builderId: new UntypedFormControl(entry.builderId, Validators.required),
            tableNumber: new UntypedFormControl(entry.tableNumber),
            count: new UntypedFormControl(entry.count, [Validators.required, Validators.pattern(/\d+/)]),
            secondaryCount: new UntypedFormControl(
                entry.secondaryCount,
                [ CustomValidators.requiredIf(_ => component.showSecondaryCount), Validators.pattern(/\d+/) ]),
            auditedCount: new UntypedFormControl(
                entry.auditedCount,
                [ CustomValidators.requiredIf(_ => component.showQuality), Validators.pattern(/\d+/) ]),
            failedCount: new UntypedFormControl(
                entry.failedCount,
                [ CustomValidators.requiredIf(_ => component.showQuality), Validators.pattern(/\d+/) ]),
            nonProductiveHours: new UntypedFormControl(
                timeFormat(entry.nonProductiveHours),
                [ CustomValidators.requiredIf(_ => component.showNonProductiveHours) ]),
            metAttendanceGoal: new UntypedFormControl(entry.metAttendanceGoal ?? (component.attendanceGoalType === PieceRateAttendanceGoalType.Penalty))
        });

        this.component = component;

        this.id = entry.id;
        this.pieceRateActivityId = entry.pieceRateActivityId

        this.prevValues = this.getRawValue();

        this.currentValues = new BehaviorSubject(this.getRawValue());

        this.subscriptions = [
            this.valueChanges.pipe(
                tap(vals => this.currentValues.next(vals)),
                debounceTime(2000)
            ).subscribe(_ => this.save()),

            combineLatest([
                component.loadingPost, component.loadingDelete, component.weekIsClosed
            ]).pipe(
                debounceTime(50) // we sometimes get multiple posts within same frame(?), which can confuse the FormGroup - SS
            ).subscribe(([loadingPost, loadingDelete, weekIsClosed]) => {
                if (loadingPost || loadingDelete || weekIsClosed) {
                    this.disable({ emitEvent: false });
                }
                else {
                    this.enable({ emitEvent: false });
                }
            }),
        ];

        this.quality = this.currentValues.pipe(map(values => calcQuality(+values.auditedCount, +values.failedCount)));

        const totalPallets = this.currentValues
            .pipe(map(values => component.showSecondaryCount ? +values.count + +values.secondaryCount : +values.count));

        this.allowedBad = totalPallets.pipe(map(total => calcAllowedBad(total, entry.qualityTarget)));

        this.afterPenalty = combineLatest([totalPallets, this.currentValues, this.allowedBad])
            .pipe(map(([total, values, allowedBad]) => calcAfterPenalty(total, values.auditedCount, values.failedCount, allowedBad, entry.penaltyMultiplier)));

        this.afterAdjustment = combineLatest([this.currentValues, totalPallets, this.afterPenalty, component.adjustmentObs])
            .pipe(map(([values, total, afterPenalty, adjustment]) => {
                if (!total)
                    return 0;
                else if (component.showQuality && +values.auditedCount)
                    return afterPenalty + adjustment;
                else
                    return total + adjustment;
            }));
    }

    destroy(): void {
        this.subscriptions.forEach(s => s.unsubscribe());
    }

    save(): void {
        if (!this.valid) {
            this.updateValueAndValidity();
            return;
        }

        const update: PieceRateAdjustmentEntryBuilder = {
            id: this.id,
            pieceRateActivityId: this.pieceRateActivityId,
            ...this.value
        };

        this.component.updateBuilderEntry(update).subscribe({
            next: _ => {
                this.prevValues = this.getRawValue();
            },
            error: _ => {
                this.setValue(this.prevValues, { emitEvent: false });
                this.currentValues.next(this.prevValues);
            }
        });
    }
}

@Component({
    selector: 'app-piece-rate-entries',
    templateUrl: './piece-rate-entries.component.html',
    styleUrls: ['./piece-rate-entries.component.scss']
})
export class PieceRateEntriesComponent implements OnInit, OnDestroy {
    _activity: BehaviorSubject<PieceRateActivity> = new BehaviorSubject<PieceRateActivity>(null);

    @Input()
    set activity(activity: PieceRateActivity) {
        this._activity.next(activity);
    }

    get isBuilderRepairs(): Observable<boolean> {
        return this._activity.pipe(map(a => [1,8,9,38].includes(a?.id)));
    }

    get activityDescription(): Observable<string> {
        return this._activity.pipe(map(activity => activity.description));
    }

    addForm: UntypedFormGroup;
    builders: Employee[] = [];
    builderSelects: SelectItem[] = [];
    tableNumber: SelectItem[] = [
        {value: null, label: '--'},
        ...Array.from(range(35)).map(i => i+1).map(i => ({ value: i, label: `${i}` }))
    ];
    site: Site;
    subscriptions: Subscription[];
    startupSubscription: Subscription;
    summaryTotalRepairs: number;
    totalBuilderRepairs: number;
    adjustmentObs: Observable<number>;
    disabled = true;
    adjustmentId: number;
    showSecondaryCount: boolean;
    showQuality: boolean;
    showMetAttendanceGoal: boolean;
    attendanceGoalType: PieceRateAttendanceGoalType;
    showNonProductiveHours: boolean;
    columnsCount: number = 0;
    defaultValueFields = {
        builder: null,
        tableNumber: null,
        count: 0,
        auditedCount: 0,
        secondaryCount: 0,
        failedCount: 0,
        quality: 0,
        nonProductiveHours: '00:00',
        metAttendanceGoal: false
    };
    displayUnit: Unit;
    defaultSort: string;
    sortOrder: number;

    weekIsClosed = new BehaviorSubject<boolean>(false);
    loadingPost = new BehaviorSubject<boolean>(false);
    loadingDelete = new BehaviorSubject<number | null>(null);

    pageName: string = 'PieceRateEntry';

    get formDataName(): Observable<string> {
        return this.activityDescription.pipe(map(activity => activity?.replace(' ', '')));
    }

    builderEntries: BuilderEntryRow[];

    constructor(private messageService: MessageService, private shiftContextService: ShiftContextService,
                private pieceRateService: PieceRateService, private activityService: ActivityBlockingService,
                private autoSaveService: AutoSaveService) {
        this.addForm = new UntypedFormGroup({
            builderId: new UntypedFormControl(null, Validators.required),
            tableNumber: new UntypedFormControl(null),
            count: new UntypedFormControl(0, Validators.required),
            secondaryCount: new UntypedFormControl(0, CustomValidators.requiredIf(_ => this.showSecondaryCount)),
            auditedCount: new UntypedFormControl(0, CustomValidators.requiredIf(_ => this.showQuality)),
            failedCount: new UntypedFormControl(0, CustomValidators.requiredIf(_ => this.showQuality)),
            quality: new UntypedFormControl({ value: 0, disabled: true }, CustomValidators.requiredIf(_ => this.showQuality)),
            nonProductiveHours: new UntypedFormControl('00:00',
                CustomValidators.requiredIf(_ => this.showNonProductiveHours)),
            metAttendanceGoal: new UntypedFormControl(false)
        });
    }

    ngOnInit(): void {
        this.builderEntries = [];

        const siteObservable = this.shiftContextService.CurrentContext.pipe(tap(context => {
            this.site = context.site;
        }));

        const buildersObservable = this.pieceRateService.CurrentBuilders.pipe(tap(builders => {
            this.builders = builders;
            this.builderSelects = builders.map(b => ({ value: b.id, label: b.fullName }));
        }));

        const buildersPieceRateAdjustmentObservable = combineLatest([
            this.pieceRateService.CurrentPieceRateAdjustment,
            this._activity
        ]).pipe(tap(([buildersPieceRateAdjustment, activity]) => {
            this.adjustmentId = null;
            this.builderEntries?.forEach(be => be.destroy());
            this.builderEntries = [];

            if (buildersPieceRateAdjustment) {
                this.weekIsClosed.next(buildersPieceRateAdjustment.weekIsClosed);

                if (buildersPieceRateAdjustment.weekIsClosed)
                    this.addForm.disable();
                else
                    this.addForm.enable();

                this.adjustmentId = buildersPieceRateAdjustment.id;
                const activityConfig = buildersPieceRateAdjustment.activityConfigs.find(ac => ac.pieceRateActivity === activity.id);

                if (buildersPieceRateAdjustment) {
                    this.showSecondaryCount = activityConfig.showSecondaryCount;
                    this.showQuality = activityConfig.showQuality;
                    this.showMetAttendanceGoal = activityConfig.showMetAttendanceGoal;
                    this.attendanceGoalType = activityConfig.attendanceGoalType;
                    this.showNonProductiveHours = activityConfig.showNonProductiveHours;
                    this.displayUnit = activityConfig.displayUnit;

                    const flags = [
                        this.showSecondaryCount,
                        this.showMetAttendanceGoal,
                        this.showNonProductiveHours,
                        this.showQuality, this.showQuality, this.showQuality, this.showQuality, this.showQuality, // affects 5 columns
                        activity.id === 1, activity.id === 1
                    ];

                    this.columnsCount = flags.filter(f => f).length + 3;

                    this.setBuilderEntries(buildersPieceRateAdjustment.builderEntries);
                }
            }
        }));

        const totalBuilderRepairsObservable = this.pieceRateService.CurrentEnteredTotal.pipe(tap(totalBuilderRepairs => {
            this.totalBuilderRepairs = totalBuilderRepairs;
        }));

        this.adjustmentObs = this.pieceRateService.CurrentAverage;

        this.subscriptions = [
            this.pieceRateService.CurrentPieceRateSummary.subscribe(summary => {
                this.summaryTotalRepairs = summary.adjustmentTotal;
            }),

            this.formDataName.subscribe(name => {
                const savedsort = this.autoSaveService.GetItem<{ field: string; order: number; }>(this.pageName, name);

                if (savedsort) {
                    this.defaultSort = savedsort.field;
                    this.sortOrder = savedsort.order;
                }
            }),

            this.shiftContextService.SiteConfig.subscribe(config => {
                const defaultNptSiteConfig = config.find(sc => sc.configKey === SiteConfig.PIECERATE_NPT_DEFAULT);

                const defNpt = defaultNptSiteConfig?.configValue ?? '00:00';

                this.addForm.controls.nonProductiveHours.setValue(defNpt);
                this.defaultValueFields.nonProductiveHours = defNpt;
            }),

            combineLatest([
                siteObservable,
                buildersObservable,
                buildersPieceRateAdjustmentObservable,
                totalBuilderRepairsObservable
            ]).pipe(
                this.activityService.Wait('Piece Rate Builder Entries')
            ).subscribe()
        ];
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(s => s.unsubscribe());
        this.builderEntries?.forEach(be => be.destroy());
        this.builderEntries = [];
    }

    setBuilderEntries(builderEntries: PieceRateAdjustmentEntryBuilder[]): void {
        const oldList = this.builderEntries ?? [];

        this.builderEntries = builderEntries.filter(b => b.pieceRateActivityId === this._activity.value.id).map(li =>
            oldList.filter(b => b.pieceRateActivityId === this._activity.value.id).find(be => be.id === li.id) ?? new BuilderEntryRow(li, this));

        oldList
            .filter(be => this.builderEntries.indexOf(be) === -1)
            .forEach(be => be.destroy());
    }

    updateBuilderEntry(item: PieceRateAdjustmentEntryBuilder): Observable<PieceRateAdjustmentEntryBuilder> {
        return zip([
            this.pieceRateService.updateBuilderEntry(this.adjustmentId, item.id, item),
            this.activityDescription.pipe(take(1))
        ]).pipe(map(([val, activityDescription]) => {
            this.messageService.add({
                severity: 'success',
                summary: `${activityDescription} Item Updated`,
                detail: ''
            });

            return val;
        }));
    }

    createBuilderEntry(): void {
        if (!this.addForm.valid) {
            this.addForm.markAllAsTouched();
            this.activityDescription.pipe(take(1)).subscribe(activityDescription => {
                this.messageService.add({
                    severity: 'warn',
                    summary: `Could not Create ${activityDescription} Item`,
                    detail: 'Fields are required.'
                });
            });
        } else {
            this.loadingPost.next(true);
            this.addForm.disable();
            const entry: PieceRateAdjustmentEntryBuilder = Object.assign({}, this.addForm.value);
            entry.pieceRateActivityId = this._activity.value.id;
            zip([
                this.pieceRateService.postBuilderEntry(this.adjustmentId, entry),
                this.activityDescription.pipe(take(1))
            ]).subscribe({
                next: ([_, activityDescription]) => {
                    this.addForm.reset(this.defaultValueFields);
                    this.messageService.add({
                        severity: 'success',
                        summary: `${activityDescription} Item Created`,
                        detail: ''
                    });
                    this.loadingPost.next(false);
                    this.addForm.enable();
                },
                error: _error => {
                    this.loadingPost.next(false);
                    this.addForm.enable();
                }
            });
        }
    }

    deleteBuilderEntry(entry: PieceRateAdjustmentEntryBuilder): void {
        this.loadingDelete.next(entry.id);
        zip([
            this.pieceRateService.deleteBuilderEntry(this.adjustmentId, entry.id),
            this.activityDescription.pipe(take(1))
        ]).subscribe({
            next: ([_, activityDescription]) => {
                this.addForm.reset(this.defaultValueFields);
                this.messageService.add({
                    severity: 'warn',
                    summary: `${activityDescription} Item Deleted`,
                    detail: ''
                });
                this.loadingDelete.next(null);
            },
            error: _error => {
                this.loadingDelete.next(null);
            }
        });
    }

    customSort(event: SortEvent): void {
        this.formDataName.pipe(take(1)).subscribe(name => {
            this.autoSaveService.SetItem(this.pageName, name, { field: event.field, order: event.order });

            event.data.sort((data1, data2) => {
                let value1 = null;
                let value2 = null;
                let result = null;

                switch(event.field) {
                    case 'builder':
                        value1 = this.builders.find(b => b.id === data1.controls.builderId.value)?.fullName;
                        value2 = this.builders.find(b => b.id === data2.controls.builderId.value)?.fullName;
                        break;
                    default:
                        value1 = data1.controls[event.field].value;
                        value2 = data2.controls[event.field].value;
                        break;
                }

                if (!value1 && value2)
                    result = -1;
                else if (value1 && !value2)
                    result = 1;
                else if (!value1 && !value2)
                    result = 0;
                else if (typeof value1 === 'string' && typeof value2 === 'string')
                    result = value1.localeCompare(value2);
                else
                    result = (value1 < value2) ? -1 : (value1 > value2) ? 1 : 0;

                return (event.order * result);
            });
        });
    }

    calculateAddFormQuality(): void {
        const count = !this.addForm.get('count').value ? 0 :
            this.addForm.get('count').value;

        let secondaryCount = 0;
        if (this.addForm.controls.secondaryCount) {
            secondaryCount = isNaN(this.addForm.get('secondaryCount').value) ? 0 :
                this.addForm.get('secondaryCount').value;
        }

        const auditedCount = !this.addForm.get('auditedCount').value
            ? 0
            : +this.addForm.get('auditedCount').value;

        let failedCount = 0;
        if (this.addForm.controls.failedCount) {
            failedCount = isNaN(this.addForm.get('failedCount').value) ? 0 : +this.addForm.get('failedCount').value;
        }

        if (this.addForm.controls.quality) {
            const qualityValue = calcQuality(auditedCount, failedCount);
            this.addForm.controls.quality.setValue(qualityValue);
        }
    }

    getUnitLabel(): string {
        switch (this.displayUnit) {
            case Unit.Pallet: return 'Pallets';
            default: return 'Pieces';
        }
    }

    rowTrackBy(_i: number, row: PieceRateAdjustmentEntryBuilder): number { return row.id; }

    onChangeNonProductiveHours(): void {
        const control = this.addForm.controls.nonProductiveHours;
        const convertedTime = timeFormat(control.value);
        control.setValue(convertedTime);
    }
}
